const API_SERVER = process.env.API_SERVER || '';

export interface CampusOption {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface StateOption {
  code: string;
  label: string;
}

interface SchoolApiRecord {
  id: string | number;
  name: string;
  address?: unknown;
  city?: unknown;
  state?: unknown;
  zip?: unknown;
}

interface SchoolLookupResponse {
  schools?: unknown;
  error?: {
    type?: string;
  };
}

interface StateApiRecord {
  state_code: string;
  state_label: string;
}

function isSchoolApiRecord(value: unknown): value is SchoolApiRecord {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const school = value as Partial<SchoolApiRecord>;
  return (typeof school.id === 'string' || typeof school.id === 'number') &&
    typeof school.name === 'string';
}

function getOptionalSchoolField(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

function isStateApiRecord(value: unknown): value is StateApiRecord {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const state = value as Partial<StateApiRecord>;
  return typeof state.state_code === 'string' && typeof state.state_label === 'string';
}

export async function getSchoolsByZip(zipCode: string): Promise<CampusOption[]> {
  const response = await fetch(
    `${API_SERVER}/schools?zipcode=${encodeURIComponent(zipCode)}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to find schools: ${response.status} ${response.statusText}`);
  }

  const result = await response.json() as SchoolLookupResponse | null;
  if (!result || typeof result !== 'object') {
    throw new Error('Failed to find schools: invalid response');
  }

  if (result.error?.type === 'not_found') {
    return [];
  }

  if (result.error || !Array.isArray(result.schools) || !result.schools.every(isSchoolApiRecord)) {
    throw new Error('Failed to find schools: invalid response');
  }

  return result.schools
    .map((school) => ({
      id: String(school.id),
      name: school.name,
      address: getOptionalSchoolField(school.address),
      city: getOptionalSchoolField(school.city),
      state: getOptionalSchoolField(school.state),
      zip: getOptionalSchoolField(school.zip),
    }))
    .sort((firstSchool, secondSchool) => firstSchool.name.localeCompare(secondSchool.name));
}

export async function getStates(): Promise<StateOption[]> {
  const response = await fetch(`${API_SERVER}/api/all-states-listed`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load states: ${response.status} ${response.statusText}`);
  }

  const result = await response.json() as unknown;
  if (!Array.isArray(result) || !result.every(isStateApiRecord)) {
    throw new Error('Failed to load states: invalid response');
  }

  return result
    .filter((state) => /^[A-Z]{2}$/.test(state.state_code))
    .map((state) => ({
      code: state.state_code,
      label: state.state_label,
    }))
    .sort((firstState, secondState) => firstState.label.localeCompare(secondState.label));
}

const SchoolService = {
  getSchoolsByZip,
  getStates,
};

export default SchoolService;
